import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, CloudLightning, Radio, ShieldAlert } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

type ScenarioId = 'delay' | 'overdue' | 'arm';

const SCENARIOS: {
  id: ScenarioId;
  label: string;
  summary: string;
  icon: typeof CloudLightning;
}[] = [
  {
    id: 'delay',
    label: 'Lightning Delay',
    summary: 'Close diamonds, reschedule games, and blast SMS to 40 coaches in 2 clicks.',
    icon: CloudLightning,
  },
  {
    id: 'overdue',
    label: 'Overdue Scores',
    summary: 'Flag missing field scores automatically when games pass the time limit.',
    icon: AlertTriangle,
  },
  {
    id: 'arm',
    label: 'Arm Care Guard',
    summary: 'Enforce pitch thresholds and rest days before a coach breaks a rule.',
    icon: ShieldAlert,
  },
];

const DIAMONDS = ['Diamond 1', 'Diamond 2', 'Diamond 3', 'Diamond 4'];

export function CommandCenterShowcase() {
  const [scenario, setScenario] = useState<ScenarioId>('delay');

  return (
    <section className="bg-slate-950 py-16 md:py-24" data-testid="command-center-showcase">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-10 max-w-3xl text-center">
          <p className="font-broadcast text-xs uppercase tracking-[0.25em] text-emerald-400">
            Director Command Center
          </p>
          <h2 className="mt-3 font-broadcast text-3xl uppercase tracking-wide text-white md:text-5xl">
            Run the weekend from one screen
          </h2>
        </div>

        <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[minmax(0,340px)_1fr] lg:items-stretch">
          <Tabs
            value={scenario}
            onValueChange={value => setScenario(value as ScenarioId)}
            className="flex flex-col"
          >
            <TabsList className="grid h-auto w-full grid-cols-1 gap-2 bg-transparent p-0">
              {SCENARIOS.map(item => {
                const Icon = item.icon;
                return (
                  <TabsTrigger
                    key={item.id}
                    value={item.id}
                    className="min-h-16 w-full items-start justify-start gap-3 whitespace-normal rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-left text-white data-[state=active]:border-emerald-400/60 data-[state=active]:bg-emerald-500/10 data-[state=active]:text-white data-[state=active]:shadow-none"
                  >
                    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" aria-hidden />
                    <span>
                      <span className="block font-broadcast text-sm uppercase tracking-wide">
                        {item.label}
                      </span>
                      <span className="mt-1 block text-xs font-normal normal-case tracking-normal text-white/70">
                        {item.summary}
                      </span>
                    </span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {SCENARIOS.map(item => (
              <TabsContent key={item.id} value={item.id} className="sr-only">
                {item.summary}
              </TabsContent>
            ))}
          </Tabs>

          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#020617] p-4 shadow-[0_20px_60px_rgba(0,0,0,0.45)] md:p-6">
            <div className="mb-4 flex items-center justify-between gap-3 border-b border-white/10 pb-3">
              <div>
                <p className="font-broadcast text-xs uppercase tracking-[0.2em] text-white/50">
                  Live ops
                </p>
                <p className="font-broadcast text-lg uppercase tracking-wide text-white">
                  Field board
                </p>
              </div>
              <span className="inline-flex min-h-11 items-center gap-2 rounded-full bg-emerald-500/15 px-3 text-xs font-broadcast uppercase tracking-wider text-emerald-300">
                <Radio className="h-4 w-4" aria-hidden />
                On air
              </span>
            </div>

            <AnimatePresence>
              {scenario === 'delay' && (
                <motion.div
                  key="weather-bar"
                  initial={{ opacity: 0, y: -12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  className="mb-4 rounded-lg bg-red-600 px-4 py-3 text-sm font-semibold text-white"
                >
                  Weather hold — Diamonds 1–4 closed. Games sliding 45 minutes.
                </motion.div>
              )}
            </AnimatePresence>

            <div className="grid grid-cols-2 gap-3">
              {DIAMONDS.map(name => {
                const overdue = scenario === 'overdue' && name === 'Diamond 2';
                return (
                  <div
                    key={name}
                    className={`rounded-xl border p-4 transition-colors ${
                      overdue
                        ? 'border-amber-400/70 bg-amber-500/15'
                        : 'border-emerald-400/30 bg-emerald-500/10'
                    }`}
                  >
                    <p className="font-broadcast text-sm uppercase tracking-wide text-white">
                      {name}
                    </p>
                    <p
                      className={`mt-1 text-xs font-semibold uppercase tracking-wider ${
                        overdue ? 'text-amber-200' : 'text-emerald-200'
                      }`}
                    >
                      {overdue ? 'Overdue: 15 min' : 'Clear'}
                    </p>
                  </div>
                );
              })}
            </div>

            <AnimatePresence>
              {scenario === 'delay' && (
                <motion.div
                  key="sms-toast"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 16 }}
                  className="mt-4 rounded-lg border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white shadow-lg"
                  role="status"
                >
                  Broadcast SMS Sent (248 recipients)
                </motion.div>
              )}
              {scenario === 'arm' && (
                <motion.div
                  key="arm-preview"
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  className="mt-4 rounded-xl border border-red-400/40 bg-slate-950 p-4 shadow-xl"
                  role="status"
                >
                  <div className="flex items-start gap-3">
                    <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-400" aria-hidden />
                    <div>
                      <p className="font-broadcast text-sm uppercase tracking-wide text-white">
                        Pitcher blocked
                      </p>
                      <p className="mt-1 text-sm text-red-200">
                        Validation Error: 2 Days Rest Required
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
